import type {ForgotRequest, LoginRequest, LogoutResponse, ResetRequest, Session} from "@/src/modules/auth/types";
import CoreService from "@/src/modules/core/services/core";

import {impersonationService} from "@/src/modules/support/services/impersonation";
import {clearGlitchTipUser} from "@/src/modules/core/lib/glitchtip";
import {useMeStore} from "@/src/modules/core/store/me";
import {purgeHelpCacheForUser} from "@/src/modules/help/lib/help-cache";

import {authApi} from "../api";
import {stampSessionLoginAt} from "../lib/stamp-session-login-at";
import {parseLogoutResponse} from "../lib/parse-logout-response";
import {useSessionEndStore} from "../store/session-end";
import {useSessionStore} from "../store/session";

class AuthService extends CoreService {
  async login(credentials: LoginRequest): Promise<Session> {
    const {data: session} = await authApi.login(credentials);
    const sessionWithLoginAt = stampSessionLoginAt(session);

    useSessionStore.setState({
      session: sessionWithLoginAt,
    });

    useSessionEndStore.getState().reset();

    return sessionWithLoginAt;
  }

  async logout(): Promise<LogoutResponse> {
    const userId = useMeStore.getState().me?.user_id;
    if (userId) {
      purgeHelpCacheForUser(userId);
    }

    const {data} = await authApi.logout();
    const result = parseLogoutResponse(data);

    impersonationService.clearOnLogout();
    clearGlitchTipUser();

    useSessionStore.setState({
      session: null,
    });

    return result;
  }

  async forgot(data: ForgotRequest): Promise<void> {
    await authApi.forgot(data);
  }

  async reset(data: ResetRequest): Promise<void> {
    await authApi.reset(data);
  }

  clearSession(): void {
    clearGlitchTipUser();

    useSessionStore.setState({
      session: null,
    });
  }
}

export const authService = new AuthService();
